import debounce from 'lodash.debounce';

import i18n from '@/i18n';
import {
  saveTargetingInfo,
  getTargetingInfo,
  deleteAllTargetingInfo,
} from '@/services/network/targetingInfo';
import { checkPostCode } from '@/services/network/postCode';
import { getDefaultTargetingInfo } from '@/services/targetingInfo';
import { getDefaultPrivateInfo } from '@/services/privateInfo';
import {
  transformTargetInfoObjectToArray,
  transformToTrackObject,
} from '@/services/targetingInfo/transformers';
import { SESSION_FINISHED } from '@/store/session/actionTypes';
import {
  SHOW_NOTIFICATION,
  SHOW_DEFAULT_ERROR_NOTIFICATION,
} from '@/store/notification/actionTypes';
import { SAVE_PRIVATE_INFO } from '@/store/privateInfo/actionTypes';
import { isCurrentAccountTest } from '@/services/user';
import { SECTION_CODES } from '@/constants';
import {
  SAVE_TARGETING_INFO,
  GET_TARGETING_INFO,
  UPDATE_TARGETING_INFO,
  CHECK_POST_CODE,
  DELETE_ALL_TARGETING_INFO,
} from './actionTypes';
import {
  SET_SAVING_STATUS,
  SET_DISPLAY_DATA,
  STASH_TARGETING_INFO_CHANGES,
  SET_USER_SEGMENTS,
  CLEAR_TARGETING_INFO,
} from './mutationTypes';

const debouncedCall = debounce((cb) => cb(), 500);

export default {
  async [GET_TARGETING_INFO]({ commit }) {
    const { displayData_dev: displayDataDev, displayData, userSegments } = await getTargetingInfo();
    commit(SET_DISPLAY_DATA, displayDataDev || displayData);
    commit(SET_USER_SEGMENTS, userSegments);
  },
  async [UPDATE_TARGETING_INFO]({ commit, getters, dispatch }, updates) {
    const [code] = Object.keys(updates);
    const value = getters.targetingInfoData[code];

    commit(STASH_TARGETING_INFO_CHANGES, updates);
    debouncedCall(() =>
      dispatch(SAVE_TARGETING_INFO).catch((e) => {
        commit(STASH_TARGETING_INFO_CHANGES, { [code]: value });
        throw e;
      }),
    );
  },
  async [SAVE_TARGETING_INFO]({ getters, dispatch, commit }, data = {}) {
    if (!getters.containsTargetingDataToSave && !Object.keys(data).length) {
      return Promise.resolve({});
    }

    const dataToSave = { ...getters.targetingInfoData, ...data };
    const changes = transformTargetInfoObjectToArray(dataToSave);

    const shouldAddTestFlag =
      isCurrentAccountTest() && !changes.includes(SECTION_CODES.TEST_ACCOUNT);
    if (shouldAddTestFlag) {
      changes.push(SECTION_CODES.TEST_ACCOUNT);
    }

    commit(SET_SAVING_STATUS, false);

    try {
      const results = await saveTargetingInfo(changes);

      // transfer data to Facebook Pixel
      const tracks = transformToTrackObject(
        getters.targetingInfoData,
        getters.targetingInfoDisplayData,
      );
      if (Object.keys(tracks).length) {
        window.fbq('trackCustom', 'SaveTargetingInfo', tracks);
      }

      commit(SET_SAVING_STATUS, true);
      return results;
    } catch (e) {
      dispatch(SHOW_NOTIFICATION, {
        type: 'error',
        message: i18n.t('update.error'),
      });
      throw new Error('Not saved');
    }
  },
  [CHECK_POST_CODE](state, postCode) {
    return checkPostCode(postCode);
  },
  async [DELETE_ALL_TARGETING_INFO]({ commit, dispatch }) {
    try {
      await deleteAllTargetingInfo();
      commit(CLEAR_TARGETING_INFO);
      dispatch(SAVE_PRIVATE_INFO, getDefaultPrivateInfo({ forceSet: false }));

      await dispatch(SAVE_TARGETING_INFO, getDefaultTargetingInfo());
      dispatch(GET_TARGETING_INFO);

      dispatch(SHOW_NOTIFICATION, {
        type: 'success',
        message: i18n.t('completed'),
      });
    } catch {
      dispatch(SHOW_DEFAULT_ERROR_NOTIFICATION);
    }
  },
  [SESSION_FINISHED]({ commit }) {
    commit(CLEAR_TARGETING_INFO);
  },
};
